import { useLayoutEffect, useState } from 'react';

/**
 * Measure the pixel width of the longest label using the provided font.
 * Falls back gracefully if canvas/font APIs aren't available.
 *
 * @param {string[]} labels
 * @param {string} font
 * @returns {number}
 */
function measureMaxLabelWidthPx(labels, font) {
  try {
    if (typeof document === 'undefined') return 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.font = font;
    let max = 0;
    for (const label of labels) {
      max = Math.max(max, ctx.measureText(label ?? '').width);
    }
    return max;
  } catch {
    return 0;
  }
}

function toPxNumber(value) {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Returns a fixed (static) dropdown control width derived from the longest option label.
 * This avoids arbitrary fixed/min widths while keeping the control stable across selections.
 *
 * @param {{
 *  buttonRef: { current: null | HTMLElement },
 *  labels: string[],
 *  minPx?: number,
 *  maxPx?: number,
 *  viewportPaddingPx?: number,
 *  bufferPx?: number,
 *  iconMarginLeftPx?: number,
 *  fallbackIconWidthPx?: number,
 * }} params
 * @returns {null | number}
 */
export function useFittedDropdownWidth({
  buttonRef,
  labels,
  minPx = 0,
  maxPx = 360,
  viewportPaddingPx = 32,
  bufferPx = 8,
  iconMarginLeftPx = 12,
  fallbackIconWidthPx = 14
}) {
  const [widthPx, setWidthPx] = useState(null);

  useLayoutEffect(() => {
    const buttonEl = buttonRef?.current;
    if (!buttonEl) return;
    if (!Array.isArray(labels) || labels.length === 0) return;
    if (typeof window === 'undefined') return;

    let rafId = null;

    const compute = () => {
      const computed = window.getComputedStyle(buttonEl);
      const font = computed.font || `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;

      const textWidth = measureMaxLabelWidthPx(labels, font);
      const paddingLeft = toPxNumber(computed.paddingLeft);
      const paddingRight = toPxNumber(computed.paddingRight);
      const borderLeft = toPxNumber(computed.borderLeftWidth);
      const borderRight = toPxNumber(computed.borderRightWidth);

      const svgEl = buttonEl.querySelector('svg');
      const iconWidth = svgEl?.getBoundingClientRect?.().width || fallbackIconWidthPx;

      const raw = Math.ceil(
        textWidth +
          paddingLeft +
          paddingRight +
          borderLeft +
          borderRight +
          iconMarginLeftPx +
          iconWidth +
          bufferPx
      );

      const viewportMax = Math.max(0, window.innerWidth - viewportPaddingPx);
      const upperBound = Math.min(maxPx, viewportMax || maxPx);
      const clamped = Math.min(Math.max(raw, minPx), upperBound);

      setWidthPx(clamped);
    };

    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(compute);
    };

    schedule();
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('resize', schedule);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    buttonRef,
    // Recompute if label set changes.
    Array.isArray(labels) ? labels.join('\n') : '',
    minPx,
    maxPx,
    viewportPaddingPx,
    bufferPx,
    iconMarginLeftPx,
    fallbackIconWidthPx
  ]);

  return widthPx;
}


